import type { Checklist } from '@cobliteam/api-lib';
import { getTemplateChecklists } from '@cobliteam/hooks/src/useEntity/useChecklist/useChecklist';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import InputSearch from '@cobliteam/ui/form/InputSearch';
import Label from '@cobliteam/ui/form/Label';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useEffect, useState } from 'react';

interface MultiSelectChecklistTemplatesProps {
  size?: 'sm' | 'md';
  selectedTemplates: Checklist[];
  onChange: (templates: Checklist[]) => void;
}

const MultiSelectChecklistTemplates = ({ selectedTemplates, onChange, size = 'sm' }: MultiSelectChecklistTemplatesProps) => {
  const { t } = useTranslation('multiselects');
  const { t: tChecklists } = useTranslation('checklists');
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState<Checklist[]>(selectedTemplates || []);
  const [search, setSearch] = useState<string | undefined>(undefined);

  const { data: templatesData, isLoading, isFetching } = getTemplateChecklists({ size: 1000 });

  const templates: Checklist[] = templatesData?.page?.content || [];

  const openDropdown = useCallback(() => {
    setDraft(selectedTemplates);
  }, [selectedTemplates]);

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setSearch(undefined);
  }, []);

  const handleClear = useCallback(() => {
    setDraft([]);
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const toggleItem = useCallback(
    (template: Checklist) => {
      const exists = draft.some((d) => d.id === template.id);
      const newDraft = exists ? draft.filter((d) => d.id !== template.id) : [...draft, template];
      setDraft(newDraft);
    },
    [draft]
  );

  const filteredTemplates = templates.filter((template) => (search ? template.name.toLowerCase().includes(search.toLowerCase()) : true));

  const hasNotChanged = selectedTemplates?.length === draft?.length && selectedTemplates.every((sel) => draft.some((d) => d.id === sel.id));

  return (
    <Dropdown
      portal={false}
      onOpenChange={openDropdown}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='flex items-center justify-between px-2'>
            <Button variant='ghost' size='sm' disabled={draft?.length === 0} onClick={handleClear}>
              {t('checklist_status.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Search className='px-2'>
            <InputSearch placeholder={t('checklist_status.search_placeholder')} value={search} autoFocus debounceInMillis={300} onChange={setSearch} className='w-full' />
          </Dropdown.Search>
          <Dropdown.Body className='px-4'>
            <Dropdown.List
              heightItem={30}
              data={filteredTemplates}
              isLoading={isLoading || isFetching}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item: Checklist) => (
                <div className='flex items-center gap-2'>
                  <Checkbox id={item.id} key={item.id} checked={draft.some((d) => d.id === item.id)} onCheckedChange={() => toggleItem(item)} />
                  <Label htmlFor={item.id} className='truncate'>
                    {item.name}
                  </Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button variant='ghost' full size='sm' onClick={handleClose}>
                {t('checklist_status.cancel')}
              </Button>
              <Button full variant='contained' size='sm' color='primary' onClick={handleApply} disabled={hasNotChanged}>
                {t('checklist_status.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' type='button' startIcon={<Icon name='layout-list' />}>
        {tChecklists('checklist')} {(selectedTemplates?.length || 0) > 0 ? `(${selectedTemplates?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectChecklistTemplates;
